// The Android AR splash (2026-10-13), matching the iOS App Clip landing: the
// wheel's brand logo, "AR Try-On", then what this browser can do — Start AR,
// install ARCore, or open the page in Chrome. Rows fade in from the bottom up
// (WAVE_FADE_MS each, a new row every WAVE_STEP_MS). The logo is the last
// row: it shows on its cue only if it is loaded by then, else the slot stays
// blank for good. Opacity only, so it is kept under Reduce Motion.
import { useEffect, useState, type CSSProperties } from 'react'
import type { SplashMode } from '@/models/ar-splash-mode'
import type { SplashBrand } from '@/services/fetch-wheel-brand'
import { buildChromeIntentUrl } from '@/lib/build-chrome-intent-url'

const WAVE_FADE_MS = 500
const WAVE_STEP_MS = 200
/** The logo's cue never comes before this (the iOS cue): a cold logo download
 *  rarely lands by the second row's turn (2026-10-13). */
const LOGO_CUE_FLOOR_MS = 1000
const ARCORE_PLAY_URL = 'https://play.google.com/store/apps/details?id=com.google.ar.core'
/** Google's own list of ARCore-certified phones. */
const ARCORE_DEVICES_URL = 'https://developers.google.com/ar/devices'
const ACCENT = '#1f6fe5'

interface ArSplashProps {
  mode: SplashMode
  brand: SplashBrand | null
  /** The AR session is starting: the button is disabled so a second tap cannot fire. */
  starting: boolean
  error: string | null
  onStart: () => void
}

type LogoStatus = 'loading' | 'loaded' | 'failed'

const wave = (rowFromBottom: number): CSSProperties =>
  ({ '--wave-delay': `${rowFromBottom * WAVE_STEP_MS}ms` }) as CSSProperties

const actionStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 10,
  width: '100%',
  minHeight: 52,
  boxSizing: 'border-box',
  padding: '0 18px',
  border: 0,
  // Shares the app's corner language (header pill + icon buttons are radius 8).
  borderRadius: 8,
  background: '#fff',
  color: ACCENT,
  font: 'inherit',
  fontSize: 17,
  fontWeight: 600,
  textDecoration: 'none',
  cursor: 'pointer',
}

const SPLASH_CSS = `
  .ar-splash-wave { opacity: 0; animation: ar-splash-wave-in ${WAVE_FADE_MS}ms ease-in-out forwards; animation-delay: var(--wave-delay, 0ms); }
  @keyframes ar-splash-wave-in { to { opacity: 1; } }
  .ar-splash-mark { opacity: 0; transition: opacity ${WAVE_FADE_MS}ms ease-in-out; }
  .ar-splash-mark[data-shown="1"] { opacity: 1; }
  @keyframes ar-splash-spin { to { transform: rotate(360deg); } }
`

/** The rows below the brand, bottom first: the action, then any message. */
function rowsFor(mode: SplashMode): number {
  return mode.kind === 'start' ? 1 : 2
}

function BrandMark({ brand, cueMs }: { brand: SplashBrand | null; cueMs: number }) {
  // The cue only opens the gate: a logo that lands after it fades in then
  // (2026-10-20, dev) rather than staying blank until a reload warms the
  // cache. A logo that fails to load leaves the slot blank.
  const [cued, setCued] = useState(false)
  const logoKey = brand?.logoURL?.toString() ?? null
  const [logo, setLogo] = useState<{ key: string | null; status: LogoStatus }>({ key: null, status: 'loading' })
  const status: LogoStatus = logo.key === logoKey ? logo.status : 'loading'

  useEffect(() => {
    const timer = setTimeout(() => setCued(true), cueMs)
    return () => clearTimeout(timer)
  }, [cueMs])

  const ready = brand != null && (brand.logoURL == null || status === 'loaded')
  const shown = cued && ready ? '1' : '0'
  const decision = status === 'failed' ? 'blank' : 'pending'
  let mark = null
  if (decision !== 'blank' && brand?.logoURL) {
    mark = (
      <img
        className="ar-splash-mark"
        data-testid="ar-splash-logo"
        data-shown={shown}
        src={brand.logoURL.toString()}
        alt={brand.name}
        onLoad={() => setLogo({ key: logoKey, status: 'loaded' })}
        onError={() => setLogo({ key: logoKey, status: 'failed' })}
        style={{ maxWidth: 240, maxHeight: 72, objectFit: 'contain' }}
      />
    )
  } else if (decision !== 'blank' && brand) {
    mark = (
      <div
        className="ar-splash-mark"
        data-testid="ar-splash-brand-name"
        data-shown={shown}
        style={{ fontSize: 44, fontWeight: 900, letterSpacing: -1, lineHeight: 1.05 }}
      >
        {brand.name}
      </div>
    )
  }
  return (
    <div
      data-testid="ar-splash-brand-slot"
      style={{ position: 'relative', top: -10, minHeight: 72, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
    >
      {mark}
    </div>
  )
}

function Spinner() {
  return (
    <span
      aria-hidden="true"
      style={{
        width: 16,
        height: 16,
        border: '2px solid currentColor',
        borderRightColor: 'transparent',
        borderRadius: '50%',
        animation: 'ar-splash-spin 700ms linear infinite',
      }}
    />
  )
}

function Action({ mode, starting, onStart }: Pick<ArSplashProps, 'mode' | 'starting' | 'onStart'>) {
  if (mode.kind === 'install-arcore') {
    return (
      <a className="ar-splash-wave" data-testid="ar-splash-install-arcore" href={ARCORE_PLAY_URL} style={{ ...actionStyle, ...wave(0) }}>
        Get it on Google Play
      </a>
    )
  }
  if (mode.kind === 'open-in-chrome') {
    return (
      <a
        className="ar-splash-wave"
        data-testid="ar-splash-open-in-chrome"
        href={buildChromeIntentUrl(window.location.href)}
        style={{ ...actionStyle, ...wave(0) }}
      >
        Open in Chrome
      </a>
    )
  }
  return (
    <button
      type="button"
      className="ar-splash-wave"
      data-testid="ar-splash-start"
      onClick={onStart}
      disabled={starting}
      aria-busy={starting}
      style={{ ...actionStyle, ...wave(0), cursor: starting ? 'default' : 'pointer' }}
    >
      {starting && <Spinner />}
      {starting ? 'Starting…' : 'Start AR'}
    </button>
  )
}

function messageFor(mode: SplashMode): string | null {
  if (mode.kind === 'install-arcore') return 'AR needs Google Play Services for AR.'
  if (mode.kind === 'open-in-chrome') return `AR isn't available in ${mode.browserName}.`
  return null
}

export function ArSplash({ mode, brand, starting, error, onStart }: ArSplashProps) {
  const rows = rowsFor(mode)
  const cueMs = Math.max(LOGO_CUE_FLOOR_MS, (rows + 1) * WAVE_STEP_MS)
  const message = messageFor(mode)
  return (
    <div
      data-testid="ar-splash"
      data-cue-ms={cueMs}
      style={{
        position: 'fixed',
        inset: 0,
        overflowY: 'auto',
        background: '#f5f5f5',
        color: '#111',
        fontFamily: 'system-ui, -apple-system, "Segoe UI", sans-serif',
        pointerEvents: 'auto',
      }}
    >
      <style>{SPLASH_CSS}</style>
      <main
        style={{
          minHeight: '100%',
          boxSizing: 'border-box',
          maxWidth: 440,
          margin: '0 auto',
          padding: '96px 28px 40px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
          <BrandMark brand={brand} cueMs={cueMs} />
          <div
            className="ar-splash-wave"
            data-testid="ar-splash-sub"
            style={{ position: 'relative', top: -20, fontSize: 16.5, fontWeight: 700, letterSpacing: 0.6, ...wave(rows) }}
          >
            AR Try-On
          </div>
        </div>
        {message != null && (
          <p
            className="ar-splash-wave"
            data-testid="ar-splash-message"
            style={{ margin: 'auto 0 0', paddingTop: 48, fontSize: 21, fontWeight: 700, lineHeight: 1.35, ...wave(1) }}
          >
            {message}
          </p>
        )}
        <div style={{ width: '100%', margin: message != null ? '40px 0 auto' : 'auto 0', display: 'flex', flexDirection: 'column', gap: 12 }}>
          <Action mode={mode} starting={starting} onStart={onStart} />
          {error != null && (
            <p data-testid="ar-splash-error" style={{ margin: 0, fontSize: 13, color: '#b3261e' }}>
              {error}
            </p>
          )}
          {/* 2026-10-30 (ROH): Chrome's raw start error gives the shopper
              nothing to try. A failed start is almost always the phone's
              ARCore support, so say that and link Google's device list. */}
          {error != null && (
            <p data-testid="ar-splash-arcore-help" style={{ margin: 0, fontSize: 13, lineHeight: 1.45, color: '#444' }}>
              AR needs Google Play Services for AR (ARCore) on a supported phone. Make sure it is installed and up to
              date in the Play Store, then reload this page.{' '}
              <a
                data-testid="ar-splash-arcore-devices-link"
                href={ARCORE_DEVICES_URL}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: ACCENT }}
              >
                See supported devices
              </a>
            </p>
          )}
        </div>
      </main>
    </div>
  )
}
